import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import {
  useGetMyGroupsQuery,
  useUnsubscribeMutation,
  useSetSubscriptionActiveMutation,
  usePauseSubscriptionMutation,
  useStartGroupTelegramLinkMutation,
} from '../../../api/subscribeApi';
import { useToast } from '../../../context/ToastContext';
import { errMsg } from '../../../utils/errMsg';
import getSubdomain from '../../../utils/getSubdomain';
import Badge from '../../../components/Badge/Badge';
import card from '../../../styles/shared/publicCard.module.css';
import buttons from '../../../styles/shared/buttons.module.css';
import table from '../../../styles/shared/table.module.css';
import styles from './SubscriptionsPage.module.css';

function isPaused(subscription) {
  return Boolean(subscription.paused_until) && new Date(subscription.paused_until) > new Date();
}

function tomorrow() {
  return new Date(Date.now() + 24 * 60 * 60 * 1000);
}

export default function SubscriptionsPage() {
  const { organizationSlug: slugFromPath } = useParams();
  const subdomainSlug = getSubdomain();
  const organizationSlug = slugFromPath ?? subdomainSlug;
  const loginPath = '/subscriber/login';
  const showToast = useToast();

  const { data, isLoading, isError, error } = useGetMyGroupsQuery(organizationSlug);
  const [setActive] = useSetSubscriptionActiveMutation();
  const [unsubscribe] = useUnsubscribeMutation();
  const [pauseSubscription, { isLoading: isPausing }] = usePauseSubscriptionMutation();
  const [startGroupTelegramLink, { isLoading: isLinkingChat }] = useStartGroupTelegramLinkMutation();

  const [pauseTarget, setPauseTarget] = useState(null);
  const [pauseDate, setPauseDate] = useState(null);

  function openPauseDialog(subscription) {
    setPauseTarget(subscription);
    setPauseDate(subscription.paused_until ? new Date(subscription.paused_until) : tomorrow());
  }

  function closePauseDialog() {
    setPauseTarget(null);
    setPauseDate(null);
  }

  async function handleSavePause() {
    if (!pauseDate) return;
    try {
      await pauseSubscription({
        organizationSlug,
        subscriptionId: pauseTarget.id,
        pausedUntil: pauseDate.toISOString(),
      }).unwrap();
      showToast('success', 'Subscriptions', `Paused till ${pauseDate.toLocaleDateString()}.`);
      closePauseDialog();
    } catch (err) {
      showToast('error', 'Subscriptions', errMsg(err, 'Could not pause subscription.'));
    }
  }

  async function handleResumeNow(subscription) {
    try {
      await pauseSubscription({
        organizationSlug,
        subscriptionId: subscription.id,
        pausedUntil: null,
      }).unwrap();
      showToast('success', 'Subscriptions', 'Subscription resumed.');
      if (pauseTarget?.id === subscription.id) closePauseDialog();
    } catch (err) {
      showToast('error', 'Subscriptions', errMsg(err, 'Could not resume subscription.'));
    }
  }

  async function handleToggleActive(subscription) {
    try {
      await setActive({
        organizationSlug,
        subscriptionId: subscription.id,
        isActive: !subscription.is_active,
      }).unwrap();
      showToast(
        'success',
        'Subscriptions',
        subscription.is_active ? 'Subscription disabled.' : 'Subscription enabled.',
      );
    } catch (err) {
      showToast('error', 'Subscriptions', errMsg(err, 'Could not update subscription.'));
    }
  }

  async function handleMessageGroup(groupId) {
    try {
      const { deep_link_url: deepLinkUrl } = await startGroupTelegramLink({
        organizationSlug,
        groupId,
      }).unwrap();
      window.open(deepLinkUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showToast('error', 'Subscriptions', errMsg(err, 'Could not open the group chat.'));
    }
  }

  async function handleUnsubscribe(group, subscription) {
    try {
      await unsubscribe({
        organizationSlug,
        groupId: group.id,
        channel: subscription.channel,
      }).unwrap();
      showToast('success', 'Subscriptions', `Unsubscribed from ${group.name}.`);
    } catch (err) {
      showToast('error', 'Subscriptions', errMsg(err, 'Could not unsubscribe.'));
    }
  }

  if (isError) {
    return (
      <div className={card.centeredPage}>
        <p className={card.status}>
          {error?.status === 401
            ? 'Your session expired.'
            : 'Could not load your subscriptions. Please try again.'}
        </p>
        <Link to={loginPath}>Log in</Link>
      </div>
    );
  }

  const rows = (data?.groups ?? [])
    .map(({ group, subscription }) => ({ id: subscription.id, group, subscription }))
    .sort((a, b) => a.group.id.localeCompare(b.group.id));

  const actionBodyTemplate = ({ group, subscription }) => (
    <div className={table.actions}>
      <Button
        label="Pause"
        icon="pi pi-calendar"
        className={styles.pauseBtn}
        onClick={() => openPauseDialog(subscription)}
        style={{ width: 'auto', height: 'auto', whiteSpace: 'nowrap' }}
      />
      <Button
        icon={`pi ${subscription.is_active ? 'pi-ban' : 'pi-check'}`}
        text
        className={subscription.is_active ? table.iconDisable : table.iconSuccess}
        onClick={() => handleToggleActive(subscription)}
      />
      <Button
        icon="pi pi-bell-slash"
        text
        className={table.iconDanger}
        onClick={() => handleUnsubscribe(group, subscription)}
      />
      <Button
        icon="pi pi-comments"
        text
        className={table.iconNeutral}
        onClick={() => handleMessageGroup(group.id)}
        disabled={isLinkingChat || !(!group.broadcast && subscription.channel === 'telegram')}
        aria-label="Message this group"
      />
    </div>
  );

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>Subscriptions</h1>
      </div>

      <div className={`${table.tableWrapper} ${table.reverseCard} ${styles.subsTable}`}>
        <DataTable
          value={rows}
          dataKey="id"
          loading={isLoading}
          emptyMessage="You're not subscribed to any groups yet."
          responsiveLayout="stack"
          rowGroupMode="rowspan"
          groupRowsBy="group.id"
        >
          <Column
            field="group.id"
            header="Group"
            className={styles.nameCell}
            body={({ group }) => (
              <div className={`${table.cellInsider} ${styles.groupNameInner}`}>{group.name}</div>
            )}
          />
          <Column
            header="Channel"
            className={styles.channelCell}
            body={({ subscription }) => (
              <div className={`${table.cellInsider} ${styles.mutedCell}`}>
                {subscription.channel}
              </div>
            )}
          />
          <Column
            header="Status"
            className={styles.statusCell}
            body={({ subscription }) => {
              const paused = isPaused(subscription);
              const statusBadge = !subscription.is_active
                ? { background: 'var(--color-badge-error-bg)', color: 'var(--color-error)', label: 'disabled' }
                : paused
                  ? {
                      background: 'var(--color-badge-warn-bg)',
                      color: 'var(--color-badge-warn-text)',
                      label: `paused till ${new Date(subscription.paused_until).toLocaleDateString()}`,
                    }
                  : { background: 'var(--color-icon-success-hover-bg)', color: 'var(--color-success)', label: 'active' };
              return (
                <div className={`${table.cellInsider} ${styles.statusInner}`}>
                  <Badge background={statusBadge.background} color={statusBadge.color}>
                    {statusBadge.label}
                  </Badge>
                </div>
              );
            }}
          />
          <Column
            header="Actions"
            className={styles.actionsCell}
            body={actionBodyTemplate}
            style={{ width: '390px' }}
          />
        </DataTable>
      </div>

      <Dialog
        header={`Pause until: ${pauseDate ? pauseDate.toLocaleDateString() : '—'}`}
        visible={Boolean(pauseTarget)}
        onHide={closePauseDialog}
        style={{ width: '360px' }}
        breakpoints={{ '640px': '90vw' }}
        className={styles.pauseDialog}
        modal
      >
        <Calendar
          inline
          value={pauseDate}
          onChange={(e) => setPauseDate(e.value)}
          minDate={tomorrow()}
          className="w-full"
          panelClassName={styles.pauseCalendarPanel}
        />
        <div className={styles.pauseDialogActions}>
          <Button
            type="button"
            label="Save"
            className={`${buttons.saveBtn} ${styles.pauseSaveBtn}`}
            onClick={handleSavePause}
            loading={isPausing}
            disabled={!pauseDate}
          />
          {pauseTarget && isPaused(pauseTarget) && (
            <Button
              type="button"
              label="Resume now"
              text
              severity="secondary"
              onClick={() => handleResumeNow(pauseTarget)}
              disabled={isPausing}
            />
          )}
        </div>
      </Dialog>
    </div>
  );
}
